import React, { useMemo, useState } from 'react';
import { CalendarRangeIcon, CheckCheckIcon, ListIcon, RefreshCwIcon } from 'lucide-react';
import { useApp } from '../../contexts/AppContext';
import { MonthCalendar } from '../../components/shared/MonthCalendar';
import { EventRow } from '../../components/member/EventRow';
import { AbsenceModal } from '../../components/member/AbsenceModal';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import type { ChoirEvent, EventType } from '../../types';
import { formatDate, organizacijskiTipi, parseDate } from '../../utils/labels';
type FilterKey = 'odgovor' | 'vsi' | 'vaje' | 'nastopi' | 'organizacijsko';
const filtri: {
  key: FilterKey;
  label: string;
  tipi?: EventType[];
}[] = [{
  key: 'odgovor',
  label: 'Čaka odgovor'
}, {
  key: 'vsi',
  label: 'Vsi'
}, {
  key: 'vaje',
  label: 'Vaje',
  tipi: ['vaja', 'intenzivna', 'sekcijska', 'generalka']
}, {
  key: 'nastopi',
  label: 'Nastopi',
  tipi: ['nastop']
}, {
  key: 'organizacijsko',
  label: 'Organizacijsko',
  tipi: organizacijskiTipi
}];

/** Skupine po mesecih, da je dolg seznam pregleden. */
function mesecKljuc(datum: string): string {
  const d = parseDate(datum);
  return d.toLocaleDateString('sl-SI', {
    month: 'long',
    year: 'numeric'
  });
}
export function MemberEvents() {
  const {
    currentMember,
    upcoming,
    visibleEvents,
    getRsvp,
    setRsvp,
    isExpected,
    pushToast,
    today
  } = useApp();
  const [filter, setFilter] = useState<FilterKey>('vsi');
  const [pogled, setPogled] = useState<'seznam' | 'koledar'>('seznam');
  const [odsotnost, setOdsotnost] = useState<ChoirEvent | null>(null);
  const me = currentMember!;
  const moji = useMemo(() => visibleEvents(me.id).filter(e => upcoming.some(u => u.id === e.id)), [visibleEvents, upcoming, me.id]);
  const brezOdgovora = useMemo(() => moji.filter(e => isExpected(e, me.id) && !getRsvp(e.id)), [moji, isExpected, getRsvp, me.id]);
  const seznam = useMemo(() => {
    if (filter === 'odgovor') return brezOdgovora;
    const izbran = filtri.find(f => f.key === filter);
    return izbran?.tipi ? moji.filter(e => izbran.tipi!.includes(e.type)) : moji;
  }, [moji, brezOdgovora, filter]);
  const skupine = useMemo(() => {
    const map = new Map<string, ChoirEvent[]>();
    seznam.forEach(e => {
      const k = mesecKljuc(e.datum);
      map.set(k, [...(map.get(k) ?? []), e]);
    });
    return [...map.entries()];
  }, [seznam]);
  function potrdiVse() {
    brezOdgovora.forEach(e => setRsvp(e.id, {
      status: 'pridem'
    }));
    pushToast(`Potrdili ste udeležbo na ${brezOdgovora.length} dogodkih.`);
  }
  return <div className="space-y-5" data-id="element-1531" data-mp="ibrzli2scmbj">
      <div className="flex flex-wrap items-end justify-between gap-3" data-id="element-1532" data-mp="if7g8pc3y3f2">
        <div data-id="element-1533" data-mp="ddi416d5fjpe">
          <h1 className="text-2xl font-semibold text-ink" data-id="element-1534" data-mp="ddqk174kj54t">Dogodki</h1>
          <p className="mt-0.5 text-sm text-ink-muted" data-id="element-1535" data-mp="334d99ak1igog">
            Vsi vaši prihajajoči dogodki — udeležbo potrdite kar tukaj.
          </p>
        </div>
        <div className="flex rounded-lg border border-line bg-white p-0.5" role="tablist" aria-label="Pogled" data-id="element-1536" data-mp="1dm6pztsyxjch">
          {([['seznam', 'Seznam', ListIcon], ['koledar', 'Koledar', CalendarRangeIcon]] as const).map(([k, label, Ikona]) => <button key={k} role="tab" aria-selected={pogled === k} onClick={() => setPogled(k)} className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[13px] font-medium transition-colors duration-150 ease-out ${pogled === k ? 'bg-green-50 text-green-700' : 'text-ink-muted hover:text-ink'}`} data-id="element-1537" data-mp="1rdcvc6ixib7a">
              <Ikona className="h-4 w-4" data-id="element-1538" data-mp="2acqv553cag5p" />
              {label}
            </button>)}
        </div>
      </div>

      {brezOdgovora.length > 0 && <Card className="flex flex-wrap items-center justify-between gap-3 border-amber-200 bg-amber-50/60 p-4" data-id="element-1539" data-mp="bz7brpjvrk3b">
          <div data-id="element-1540" data-mp="3gtdszofit3bt">
            <p className="text-sm font-medium text-ink" data-id="element-1541" data-mp="3gt4a0ksw7wfm">
              {brezOdgovora.length} dogodkov še čaka vaš odgovor
            </p>
            <p className="mt-0.5 text-[13px] text-ink-muted" data-id="element-1542" data-mp="3crivyw4mhc3u">
              Prvi je {formatDate(brezOdgovora[0].datum, {
            weekday: true
          })}.
            </p>
          </div>
          <Button size="sm" onClick={potrdiVse} data-id="element-1543" data-mp="3lts2lawrslby">
            <CheckCheckIcon className="h-4 w-4" data-id="element-1544" data-mp="3lqxhri9ugyi0" />
            Pridem na vse
          </Button>
        </Card>}

      {pogled === 'koledar' ? <div className="space-y-3" data-id="element-1545" data-mp="2t54cn212t7s2">
          <MonthCalendar events={moji} detailBase="/clan/dogodki" today={today} data-id="element-1546" data-mp="2t1zzvg9jp3ps" />
          <button onClick={() => pushToast('Povezava za sinhronizacijo koledarja je bila pripravljena.')} className="inline-flex items-center gap-2 rounded-lg border border-line bg-white px-3 py-2 text-[13px] font-medium text-ink transition-colors duration-150 ease-out hover:bg-canvas" data-id="element-1547" data-mp="37uk0oa982z42">
            <RefreshCwIcon className="h-4 w-4" data-id="element-1548" data-mp="2abh5cc58qsp8" />
            Sinhronizacija koledarja
          </button>
        </div> : <>
      <div className="flex flex-wrap gap-2" data-id="element-1549" data-mp="umsuy0msgme2">
        {filtri.map(f => {
          const stevec = f.key === 'odgovor' ? brezOdgovora.length : undefined;
          if (f.key === 'odgovor' && brezOdgovora.length === 0) return null;
          return <button key={f.key} onClick={() => setFilter(f.key)} aria-pressed={filter === f.key} className={`rounded-lg border px-3 py-1.5 text-[13px] font-medium transition-colors duration-150 ease-out ${filter === f.key ? 'border-green-500 bg-green-500 text-white' : 'border-line bg-white text-ink-muted hover:bg-canvas hover:text-ink'}`} data-id="element-1550" data-mp="kudamwo0rlq0">
              {f.label}
              {stevec !== undefined && ` (${stevec})`}
            </button>;
        })}
      </div>

      {seznam.length === 0 ? <Card className="p-10 text-center" data-id="element-1551" data-mp="1uuz7squukdbt">
          <p className="text-sm font-medium text-ink" data-id="element-1552" data-mp="1uto4es5bd7l8">
            {filter === 'odgovor' ? 'Odgovorili ste na vse dogodke.' : 'Za izbrani filter ni dogodkov.'}
          </p>
          <p className="mt-1 text-[13px] text-ink-muted" data-id="element-1553" data-mp="1l3l4e7zrytde">Poskusite z drugim filtrom.</p>
        </Card> : <div className="space-y-6" data-id="element-1554" data-mp="1h2jckc86tjlg">
          {skupine.map(([mesec, dogodki]) => <section key={mesec} data-id="element-1555" data-mp="1c4zqiq5902iy">
              <h2 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-ink-muted" data-id="element-1556" data-mp="1c1vcz25w7ufm">
                {mesec}
              </h2>
              <div className="space-y-2.5" data-id="element-1557" data-mp="1250egj9mdu1c">
                {dogodki.map(e => <EventRow key={e.id} event={e} rsvp={getRsvp(e.id)} to={`/clan/dogodki/${e.id}`} expected={isExpected(e, me.id)} onPridem={() => {
              setRsvp(e.id, {
                status: 'pridem'
              });
              pushToast('Udeležba je potrjena.');
            }} onNeMorem={() => setOdsotnost(e)} data-id="element-1558" data-mp="y4givk4p2yhb" />)}
              </div>
            </section>)}
        </div>}
        </>}

      {odsotnost && <AbsenceModal event={odsotnost} open onClose={() => setOdsotnost(null)} data-id="element-1559" data-mp="s23idqh09642" />}
    </div>;
}